import React from 'react';
import { getClassNames } from '../../tools/helpers';

// Which gate is holding a member out of tonight's run, in the operator's words.
// The distinction matters because the two have different remedies: one is a
// switch on this page, the other is a Flipper rollout the operator does not
// control from here. "Not running" alone would send them looking in the wrong
// place.
const HELD_BY_REASON = {
  excluded_here: 'switched off here',
  region_disabled: 'region disabled',
  flag_off: 'flag off',
};

/**
 * One member company of a region, and whether it will actually run.
 *
 * A member runs only when three gates agree — the region is enabled, the
 * membership is included, and the Flipper flag is on for that company — which
 * is the same rule Gmpro::CandidateLoader applies when it builds a run. The row
 * names the gate rather than reporting a bare "not running", so the operator
 * knows whether the fix is here or somewhere else.
 *
 * @param {Object} props
 * @param {Object} props.member - One entry from the controller's members_payload:
 *   { company_id, name, gmpro_included, held_by }, where held_by is
 *   "excluded_here", "flag_off", or null.
 * @param {function(number, boolean): void} props.onToggle - called with the
 *   company id and the requested inclusion.
 * @returns {JSX.Element}
 */
export default function MemberRow({ member, onToggle }) {
  const {
    company_id: companyId, name, gmpro_included: included, held_by: heldBy,
  } = member;
  const reason = HELD_BY_REASON[heldBy];

  return (
    <li className="gmpro-member" data-testid={`gmpro-member-${companyId}`}>
      {/*
        A switch, not a checkbox: this reads as a setting that is on or off for
        a region, and the design system draws that as a pill. role="switch"
        keeps it a real control for anyone not looking at the pill.
      */}
      <button
        type="button"
        role="switch"
        aria-checked={included}
        aria-label={`Include ${name}`}
        className={getClassNames('gmpro-member__switch', {off: !included})}
        onClick={() => onToggle(companyId, !included)}
        data-testid={`gmpro-member-toggle-${companyId}`}
      >
        <i />
      </button>

      <span className={getClassNames('gmpro-member__who', {dim: !included})}>
        <b className="gmpro-member__name">{name}</b>
        {reason ? (
          <small className="gmpro-member__held" data-testid={`gmpro-member-held-${companyId}`}>
            Not running — {reason}
          </small>
        ) : (
          <small className="gmpro-member__running">running</small>
        )}
      </span>
    </li>
  );
}
